import { LitElement, html, css } from 'lit';
import { closeIcon } from './icons/close';
import { share } from './icons/share';
import { admin } from './icons/admin';
import { content } from './icons/content';
import { ondemand } from './icons/ondemand';
import { players } from './icons/players';
import { playlists } from './icons/playlists';
import { programs } from './icons/programs';
import { schedules } from './icons/schedules';
import { trashIcon } from './icons/trashIcon';
import { dropdownIndicatorIcon } from './icons/dropdownIndicatorIcon';
import { shareFileModalStyles } from './css/sharing.css.js';

class ShareFileModal extends LitElement {
  static properties = {
    open: { type: Boolean },
    product: { type: Object },
    sharedAccounts: { type: Array },
    initSharedAccounts: { type: Array },
    sharedAccount: { type: String },
    allAccounts: { type: Array },
    addingAccount: { type: Boolean },
    dropdownOpenIndex: { type: Number },
    ownerAccount: { type: Object, hasChanged: (oldVal, newVal) => JSON.stringify(oldVal) !== JSON.stringify(newVal) }
  };

  constructor() {
    super();
    this.product = { name: '', id: null, type: '' };
    this.sharedAccounts = [];
    this.initSharedAccounts = [];
    this.allAccounts = [].sort((a, b) => a.name.localeCompare(b.name));
    this.addingAccount = false;
    this.dropdownOpenIndex = -1;
    this.ownerAccount = { id: null, name: '', description: '', is_locked: false };
    this.defaultAccountPermission = 'Can View';
    this.sharedAccount = 'false';
  }
  
  static styles = shareFileModalStyles;

  firstUpdated() {
    this.disableShareButton('Save')
  }

  getIconForProduct(name) {
    const iconMap = {
      admin: admin,
      content: content,
      vod_group: ondemand,
      player: players,
      playlist: playlists,
      program: programs,
      schedule: schedules
    };
    return iconMap[name] || share;
  }

  toggleAddAccount(event) {
    event.stopPropagation();
    this.addingAccount = !this.addingAccount;
  }

  toggleDropdown(index) {
    this.dropdownOpenIndex = this.dropdownOpenIndex === index ? -1 : index;
  }

  observeAccounts(){
    if (this.accountsChanged()) {
      this.disableShareButton('Save');
    }else{
      this.enableShareButton();
    }
  }

  selectDefaultPermission(option) {
    if (this.sharedAccount === 'true' && option === 'Can Edit') {
      this.defaultAccountPermission = 'Can Edit';
    } else {
      this.defaultAccountPermission = 'Can View';
    }
    this.dropdownOpenIndex = -1;
    this.requestUpdate();
  }

  selectOption(index, option) {  
    this.sharedAccounts[index].is_locked = option === 'Can View';
    this.dropdownOpenIndex = -1;
    this.sharedAccounts = [...this.sharedAccounts];
    this.observeAccounts()
  }

  handleAccountSelection(event, accountId) {
    const isChecked = event.target.checked;
    if (isChecked) {
      const accountToAdd = this.allAccounts.find(account => account.id === accountId);
      if (accountToAdd && !this.sharedAccounts.some(account => account.id === accountId)) {
        this.sharedAccounts = [
          ...this.sharedAccounts,
          {
            ...accountToAdd,
            is_locked: this.defaultAccountPermission === 'Can View',
          },
        ].sort((a, b) => a.name.localeCompare(b.name));
      }
    } else {
      this.sharedAccounts = this.sharedAccounts.filter(account => account.id !== accountId);
    }

    this.observeAccounts()
  }

  handleSelectAll(event) {
    const isChecked = event.target.checked;
    if (isChecked) {
      this.allAccounts
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach(account => {
          if (!this.sharedAccounts.some(shared => shared.id === account.id)) {
            this.sharedAccounts = [
              ...this.sharedAccounts,
              { ...account, is_locked: true },
            ];
          }
      });
    } else {
      const allAccountIds = this.allAccounts.map(account => account.id);
      this.sharedAccounts = this.sharedAccounts.filter(
        shared => !allAccountIds.includes(shared.id)
      );
    }
    this.observeAccounts()
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
  }

  handleDocumentClick = (event) => {
    if (!this.open) return;
  
    const modal = this.shadowRoot.querySelector('.modal');
    const dropdowns = this.shadowRoot.querySelectorAll('.dropdown');
    const clickTarget = event.composedPath()[0];

    if (modal && !modal.contains(clickTarget)) {
      this._close();
      return;
    }

    let clickInsideDropdown = false;
    dropdowns.forEach(dropdown => {
      if (dropdown.contains(clickTarget)) {
        clickInsideDropdown = true;
      }
    });

    if (!clickInsideDropdown && this.dropdownOpenIndex !== -1) {
      this.dropdownOpenIndex = -1;
      this.requestUpdate();
    }
  };

  filterSharedAccounts() {
    return this.sharedAccounts.map(account => ({
      account_id: account.id,
      is_locked: account.is_locked,
    }));
  }

  disableShareButton(message){
    let shareButton = this.shadowRoot.querySelector('.btn.primary');
    if (!shareButton) return;
    shareButton.disabled = true;
    shareButton.textContent = message;
  }

  enableShareButton(){
    let shareButton = this.shadowRoot.querySelector('.btn.primary');
    if (!shareButton) return;
    shareButton.disabled = false;
    shareButton.textContent = 'Save';
  }

  shareFile(){
    this.disableShareButton('Saving...')

    $.ajax({
      method: 'post',
      url: Routes.update_shares_path(this.product.id),
      beforeSend: function (xhr) {
        xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'));
      },
      contentType: 'application/json',
      data: JSON.stringify({
        sharedAccounts: this.filterSharedAccounts(),
        type: this.product.type
      }),
      success: () => {
        this.open = false;
      },
      error: (xhr) => {
        this.enableShareButton()
        console.error('Error sharing file:', xhr.responseText);
      }
    });
  }

  renderAccountsList() {
    return html`
      <div class="accounts-section">
        <div class="divider"></div>
        <div class="label">Accounts</div>
        <div class="accounts-table">
          <!-- Table Header -->
          <div class="table-header">
            <div class="table-column account-column">Account</div>
            <div class="table-column access-column">Access</div>
          </div>
          <!-- Table Rows -->
          <div class="table-body">
            <!-- Main Account Row -->
            <div class="table-row main-account">
              <div class="table-column account-column">${this.ownerAccount.name}</div>
              <div class="table-column access-column">Owner</div>
            </div>
            <!-- Shared Accounts Rows -->
            ${this.sharedAccounts.sort((a, b) => a.name.localeCompare(b.name)).map(
              (account, index) => html`
                <div class="table-row">
                  <div class="table-column account-column">${account.name}</div>
                  <div class="table-column access-column">
                    <div class="dropdown">
                      <span @click=${() => this.toggleDropdown(index)}>
                        ${account.is_locked ? 'Can View' : 'Can Edit'}
                        ${dropdownIndicatorIcon}
                      </span>
                      <ul class="dropdown-options ${this.dropdownOpenIndex === index ? 'open' : ''}">
                        <li @click=${() => this.selectOption(index, 'Can View')}>Can View</li>
                        ${this.sharedAccount === 'true'
                          ? html`<li @click=${() => this.selectOption(index, 'Can Edit')}>Can Edit</li>`
                          : ''}
                      </ul>
                    </div>
                    <span
                      class="trash-icon"
                      @click=${(event) => {
                        event.stopPropagation();
                        this.removeAccount(index);
                      }}
                      title="Remove Account"
                    >
                      ${trashIcon}
                    </span>
                  </div>
                </div>
              `
            )}
          </div>
          <!-- Footer Row -->
          <div class="table-footer"></div>
        </div>
      </div>
    `;
  }

  updated(changedProperties) {
    if (changedProperties.has('sharedAccount')) {
      this.sharedAccount = this.sharedAccount === 'true' ? 'true' : 'false';
    }

  }
  renderAddAccount() {
    return html`
      <div class="list add-account" tabindex="0">
        <div class="display-trigger scrollable-section">
          <label class="check-all" @click=${event => event.stopPropagation()}>
            <input
              type="checkbox"
              @change=${this.handleSelectAll}
              .checked=${this.sharedAccounts.length === this.allAccounts.length}
            />
            Check All
          </label>
          <div class="list-item">
            <div class="spacer"></div>
            <div class="account-initial account-owner-icon">
              ${this.ownerAccount.name[0].toUpperCase()}
            </div>
            <div class="account-details">
              <div class="account-name">${this.ownerAccount.name}</div>
              <div class="account-description">${this.ownerAccount.description}</div>
            </div>
            <div class="account-owner">Owner</div>
          </div>
          ${this.allAccounts
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(account => html`
              <div class="list-item" @click=${event => event.stopPropagation()}>
                <div class="spacer">
                  ${account.id !== this.ownerAccount.id
                    ? html`
                        <input
                          class="account-checkbox"
                          type="checkbox"
                          @change=${event => this.handleAccountSelection(event, account.id)}
                          .checked=${this.sharedAccounts.some(shared => shared.id === account.id)}
                        />
                      `
                    : ''}
                </div>
                <div class="account-initial">
                  ${account.name[0].toUpperCase()}
                </div>
                <div class="account-details">
                  <div class="account-name">${account.name}</div>
                  <div class="account-description">${account.description}</div>
                </div>
                ${account.id === this.ownerAccount.id
                  ? html`<div class="account-owner">Owner</div>`
                  : ''}
              </div>
            `
          )}
        </div>      
      </div>
    `;
  }

  render() {
    return html`
      <div class="wrapper ${this.open ? 'open' : ''}" aria-hidden="${!this.open}">
        ${this.open
          ? html`
              <div class="overlay">
                <div class="modal">
                  <div class="header">Share</div>
                  <div class="content">
                    <div class="label">Add Account(s)</div>
                    <div class="dropdown-section">
                      <!-- First Dropdown -->
                      <div class="dropdown">
                        <span @click=${() => this.toggleDropdown('default')}>
                          ${this.defaultAccountPermission}
                          <span class="dropdown-indicator">&#x25BE;</span>
                        </span>
                        <ul class="dropdown-options ${this.dropdownOpenIndex === 'default' ? 'open' : ''}">
                          <li @click=${() => this.selectDefaultPermission('Can View')}>Can View</li>
                          ${this.sharedAccount === 'true'
                            ? html`<li @click=${() => this.selectDefaultPermission('Can Edit')}>Can Edit</li>`
                            : ''}
                        </ul>
                      </div>
                      <!-- Second Dropdown -->
                      <div class="dropdown">
                        <span @click=${() => this.toggleDropdown('accounts')}>
                          Accounts
                          <span class="dropdown-indicator">&#x25BE;</span>
                        </span>
                        <ul class="dropdown-options ${this.dropdownOpenIndex === 'accounts' ? 'open' : ''}">
                          ${this.allAccounts.map(
                            account => html`
                              <li>
                                <label>
                                  <input
                                    type="checkbox"
                                    @change=${event => this.handleAccountSelection(event, account.id)}
                                    .checked=${this.sharedAccounts.some(shared => shared.id === account.id)}
                                  />
                                  ${account.name}
                                </label>
                              </li>
                            `
                          )}
                        </ul>
                      </div>
                    </div>
                    ${this.addingAccount
                      ? this.renderAddAccount()
                      : this.renderAccountsList()}
                  </div>
                  <div class="footer">
                    <button class="btn primary" @click=${this.shareFile}>
                      Save
                    </button>
                    <button class="btn cancel" @click=${this._close}>Cancel</button>
                  </div>
                </div>
              </div>
            `
          : ''}
      </div>
    `;
  }

  _closeIcon() {
    return html`
      <span class="close-icon" @click=${this._close}>${closeIcon}</span>
    `;
  }
  _share() {
    return html`
      <span class="share">${share}</span>
    `;
  }

  _close() {
    this.open = false;
  }
  removeAccount(index) {
    this.sharedAccounts.splice(index, 1);
    this.sharedAccounts = [...this.sharedAccounts];
    this.observeAccounts()
  }

  accountsChanged() {
    let key = 'id'
    if (this.initSharedAccounts.length !== this.sharedAccounts.length) return false;
  
    const sortByKey = (arr) =>
      [...arr].sort((x, y) => (x[key] > y[key] ? 1 : -1));
  
    const sortedinitSharedAccounts = sortByKey(this.initSharedAccounts);
    const sortedsharedAccounts = sortByKey(this.sharedAccounts);
  
    return sortedinitSharedAccounts.every((item, index) => {
      return JSON.stringify(item) === JSON.stringify(sortedsharedAccounts[index]);
    });
  }
}


customElements.define('share-file-modal', ShareFileModal);


